import type { FC } from 'react';
import { useEffect } from 'react';

import { useFieldArray, useFormContext } from 'react-hook-form';
import type { ZodIssue } from 'zod';
import { z } from 'zod';

import { TrashCanIcon } from '@polyai/ui/components/atoms/Icons';
import { Stack } from '@polyai/ui/components/atoms/Stack';

import { FormSelect } from 'components/molecules/Form';
import { FormTextInput } from 'components/molecules/Form/FormTextInput';
import type { ModalSchema } from 'screens/Handoff/components/FormHandoffModal';
import { HANDOFF } from 'screens/Handoff/Handoff.uiCopy';
import { useIntegrations } from 'screens/Integrations/hooks/useIntegrations';
import * as Styled from './AddEditHandoffStreamModalContent.styled';

const sipIntive = z.literal('invite');
const sipRefer = z.literal('refer');
const sipBye = z.literal('bye');

const sipMethod = z.union([sipIntive, sipRefer, sipBye]);

type SipMethod = z.infer<typeof sipMethod>;

type SimMethodOptions = {
  value: SipMethod;
  label: string;
};

type EncryptionOptions = {
  value: string;
  label: string;
};

export const SIP_METHOD_OPTIONS: SimMethodOptions[] = [
  {
    value: 'invite',
    label: 'SIP INVITE',
  },
  {
    value: 'refer',
    label: 'SIP REFER',
  },
  {
    value: 'bye',
    label: 'SIP BYE',
  },
];

export const ENCRYPTION_OPTIONS: EncryptionOptions[] = [
  {
    value: 'UDP/RTP',
    label: 'UDP/RTP',
  },
  {
    value: 'TLS/SRTP',
    label: 'TLS/SRTP',
  },
];

type Props = {
  readonly?: boolean;
  errors?: ZodIssue[];
};

const FormAddEditHandoffModalContent: FC<Props> = ({ readonly, errors }) => {
  const { control, watch, setValue, getValues } = useFormContext<ModalSchema>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sipHeaders',
  });

  const { activeTelephonyIntegration } = useIntegrations();

  const onInputChange = (value: string, idx: number) => {
    if (!value) {
      return;
    }
    const isLast = idx === fields.length - 1;
    if (!isLast) {
      return;
    }

    const headers = getValues('sipHeaders');
    const currentHeader = headers[idx];

    if (!currentHeader?.key || !currentHeader?.value) {
      return;
    }

    setValue(`sipHeaders.${idx}.isLast`, false);
    append(
      { key: '', value: '', isLast: true },
      {
        shouldFocus: false,
      },
    );
  };

  const method = watch('method');

  useEffect(() => {
    setValue('methodType', method.value as SipMethod);
  }, [method.value, setValue]);

  const showRouteInput =
    method.value !== 'bye' &&
    !activeTelephonyIntegration?.additionalConfig?.noConfigurableRoute;

  const isConfigurableHandoffMethod =
    !activeTelephonyIntegration?.additionalConfig?.noConfigurableHandoffMethod;

  const hasConfigurableSIPHeaders =
    !activeTelephonyIntegration?.additionalConfig?.noConfigurableSIPHeaders;

  return (
    <>
      <FormTextInput<ModalSchema>
        controlKey="name"
        label={HANDOFF.modal.add.form.destinationInput.label}
        placeholder={HANDOFF.modal.add.form.destinationInput.placeholder}
        readOnly={readonly}
        data-1p-ignore
      />

      <Styled.TextAreaInput
        controlKey="description"
        label={HANDOFF.modal.add.form.descriptionInput.label}
        placeholder={HANDOFF.modal.add.form.descriptionInput.placeholder}
        readOnly={readonly}
        textArea
      />

      <FormSelect<ModalSchema>
        controlKey="method"
        help={HANDOFF.modal.add.form.methodInput.helpText}
        label={HANDOFF.modal.add.form.methodInput.label}
        options={SIP_METHOD_OPTIONS}
        readonly={readonly || !isConfigurableHandoffMethod}
        isDisabled={!isConfigurableHandoffMethod}
      />

      {method.value === 'invite' && (
        <>
          <FormSelect<ModalSchema>
            controlKey="encryption"
            help={HANDOFF.modal.add.form.encryptionInput.helpText}
            label={HANDOFF.modal.add.form.encryptionInput.label}
            options={ENCRYPTION_OPTIONS}
            readonly={readonly}
          />

          <FormTextInput<ModalSchema>
            controlKey="integrationName"
            help={HANDOFF.modal.add.form.integrationInput.helpText}
            label={HANDOFF.modal.add.form.integrationInput.label}
            placeholder={HANDOFF.modal.add.form.integrationInput.placeholder}
            readOnly={readonly}
          />
        </>
      )}
      {showRouteInput && (
        <FormTextInput<ModalSchema>
          controlKey="route"
          help={HANDOFF.modal.add.form.routeInput.helpText}
          label={HANDOFF.modal.add.form.routeInput.label}
          placeholder={HANDOFF.modal.add.form.routeInput.placeholder}
          readOnly={readonly}
        />
      )}

      {hasConfigurableSIPHeaders && (
        <Stack direction="column" spacing="xs2">
          {fields.map((field, idx) => (
            <Stack key={field.id} direction="row" spacing="xs2">
              <Styled.InputWrapper>
                <FormTextInput<ModalSchema>
                  controlKey={`sipHeaders.${idx}.key`}
                  help={HANDOFF.modal.add.form.sipHeadersInput.helpText}
                  label={
                    idx === 0 && HANDOFF.modal.add.form.sipHeadersInput.label
                  }
                  placeholder={
                    HANDOFF.modal.add.form.sipHeadersInput.placeholder
                  }
                  readOnly={readonly}
                  onChange={e => {
                    const value = e.target.value;
                    onInputChange(value, idx);
                  }}
                />
              </Styled.InputWrapper>
              <Styled.InputWrapper>
                <FormTextInput<ModalSchema>
                  controlKey={`sipHeaders.${idx}.value`}
                  label={idx === 0 && HANDOFF.modal.add.form.valueInput.label}
                  placeholder={HANDOFF.modal.add.form.valueInput.placeholder}
                  readOnly={readonly}
                  onChange={e => {
                    const value = e.target.value;
                    onInputChange(value, idx);
                  }}
                />
              </Styled.InputWrapper>
              {!readonly && (
                <Styled.DeleteIcon
                  $isFirst={idx === 0}
                  disabled={idx === fields.length - 1}
                  onClick={() => {
                    remove(idx);
                  }}
                >
                  <TrashCanIcon />
                </Styled.DeleteIcon>
              )}
            </Stack>
          ))}
        </Stack>
      )}
      {errors &&
        errors.map((error: any) => (
          <p key={error.path} style={{ color: 'red' }}>
            {error.path.join('.')} {error.message}
          </p>
        ))}
    </>
  );
};

export default FormAddEditHandoffModalContent;
